import React, { useState } from 'react';
import {
    Paper,
    Box,
    Typography,
    Stack,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, LockOpen } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import FormInput from '../inputs/FormInput';
import SwitchInput from '../inputs/SwitchInput';
import { usePermissions } from '../../auth/PermissionsContext';
import { getSettings } from '../../api/apiSettings';

// The masked sentinel returned by the backend when secrets are NOT revealed.
// We treat any value still equal to MASK as "untouched" so it isn't sent on save.
export const MASK_SENTINEL = '****';

const FIELD_KEYS = ['api_key', 'integration_id', 'iframe_id', 'hmac_secret', 'sandbox'];

const SecretField = ({ label, value, onChange, disabled, helperText, isArabic }) => {
    const [show, setShow] = useState(false);
    const isMasked = value === MASK_SENTINEL;

    return (
        <Box>
            <Typography
                variant="body2"
                fontWeight="bold"
                color="text.primary"
                sx={{ mb: 0.5 }}
            >
                {label}
            </Typography>
            <TextField
                fullWidth
                size="small"
                type={show ? 'text' : 'password'}
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                placeholder={isMasked ? (isArabic ? 'محفوظ — اضغط "كشف" لتعديله' : 'Saved — click "Reveal" to edit') : ''}
                autoComplete="new-password"
                helperText={helperText}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                size="small"
                                onClick={() => setShow((s) => !s)}
                                edge="end"
                                tabIndex={-1}
                            >
                                {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
        </Box>
    );
};

const PaymobSettingsCard = ({ data, onChange, disabled }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const { has } = usePermissions();

    const canReveal = has('settings_reveal_secrets');

    const [revealing, setRevealing] = useState(false);
    const [revealed, setRevealed] = useState(false);

    const safe = data && typeof data === 'object' ? data : {};

    const handleField = (key) => (val) => {
        onChange({ ...safe, [key]: val });
    };

    const handleReveal = async () => {
        if (!canReveal || revealing) return;
        try {
            setRevealing(true);
            const res = await getSettings({ reveal: true });
            const revealedSettings = res?.data || {};
            const revealedPaymob = revealedSettings?.payment?.paymob || {};

            // Replace masked values with the unmasked ones; preserve any
            // local edits the user has already typed (i.e. fields whose
            // current value is not the sentinel).
            const next = { ...safe };
            FIELD_KEYS.forEach((k) => {
                if (next[k] === MASK_SENTINEL || next[k] === undefined) {
                    next[k] = revealedPaymob[k] ?? next[k];
                }
            });
            onChange(next);
            setRevealed(true);
            toast.success(isArabic ? 'تم كشف الأسرار' : 'Secrets revealed');
        } catch (err) {
            toast.error(
                err?.response?.data?.message ||
                (isArabic ? 'تعذر كشف الأسرار' : 'Failed to reveal secrets')
            );
        } finally {
            setRevealing(false);
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                mb: 3,
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1.5}
                mb={2}
            >
                <Box>
                    <Typography variant="h6" fontWeight={700}>
                        Paymob
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isArabic
                            ? 'بيانات اعتماد بوابة الدفع Paymob.'
                            : 'Paymob payment gateway credentials.'}
                    </Typography>
                </Box>

                <Tooltip
                    title={
                        canReveal
                            ? ''
                            : isArabic
                                ? 'تحتاج صلاحية settings.reveal_secrets'
                                : 'Requires settings.reveal_secrets permission'
                    }
                >
                    <span>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={revealing ? <CircularProgress size={14} /> : <LockOpen fontSize="small" />}
                            disabled={!canReveal || revealing || revealed}
                            onClick={handleReveal}
                        >
                            {revealed
                                ? (isArabic ? 'تم الكشف' : 'Revealed')
                                : (isArabic ? 'كشف الأسرار' : 'Reveal secrets')}
                        </Button>
                    </span>
                </Tooltip>
            </Stack>

            <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                    {isArabic
                        ? 'القيم المحفوظة هنا تتجاوز إعدادات .env. التغييرات تطبَّق فوراً على عمليات الدفع الجديدة.'
                        : 'Paymob credentials saved here override the values in .env. Changes take effect immediately for new payment flows.'}
                </Typography>
            </Alert>

            {/* DA-3 — soft warning when the admin is about to save blank
                production credentials. We key off `sandbox === false` as the
                production indicator (api_key+integration_id are the two
                Paymob requires to start a transaction). The masked sentinel
                means the value IS saved server-side, so it's never empty. */}
            {(() => {
                const apiKeyMissing = !safe.api_key || safe.api_key === '';
                const integrationMissing = !safe.integration_id;
                const isProd = safe.sandbox === false;
                if (!isProd) return null;
                if (!apiKeyMissing && !integrationMissing) return null;
                return (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                            {isArabic
                                ? 'تنبيه: بيانات اعتماد Paymob فارغة في وضع الإنتاج'
                                : 'Warning: Paymob credentials are blank in production'}
                        </Typography>
                        <Typography variant="body2">
                            {isArabic
                                ? 'حفظ هذه الإعدادات سيعطّل الدفع الإلكتروني لجميع العملاء حتى تُستعاد البيانات.'
                                : "Saving these settings will disable online payments for all customers until the credentials are restored."}
                        </Typography>
                    </Alert>
                );
            })()}

            <Stack spacing={2.5}>
                <SecretField
                    label={isArabic ? 'مفتاح API' : 'API Key'}
                    value={safe.api_key}
                    onChange={handleField('api_key')}
                    disabled={disabled}
                    isArabic={isArabic}
                    helperText={
                        safe.api_key === MASK_SENTINEL
                            ? (isArabic
                                ? 'القيمة محفوظة. اضغط "كشف الأسرار" لتعديلها.'
                                : 'Value is saved. Click "Reveal secrets" to edit it.')
                            : ''
                    }
                />

                <FormInput
                    type="text"
                    label={isArabic ? 'معرف التكامل (Integration ID)' : 'Integration ID'}
                    name="integration_id"
                    value={safe.integration_id || ''}
                    onChange={(e) => handleField('integration_id')(e.target.value)}
                    disabled={disabled}
                    required={false}
                    placeholder=""
                />

                <FormInput
                    type="text"
                    label={isArabic ? 'معرف الإطار (iFrame ID)' : 'iFrame ID'}
                    name="iframe_id"
                    value={safe.iframe_id || ''}
                    onChange={(e) => handleField('iframe_id')(e.target.value)}
                    disabled={disabled}
                    required={false}
                    placeholder=""
                />

                <SecretField
                    label={isArabic ? 'مفتاح HMAC' : 'HMAC Secret'}
                    value={safe.hmac_secret}
                    onChange={handleField('hmac_secret')}
                    disabled={disabled}
                    isArabic={isArabic}
                    helperText={
                        safe.hmac_secret === MASK_SENTINEL
                            ? (isArabic
                                ? 'القيمة محفوظة. اضغط "كشف الأسرار" لتعديلها.'
                                : 'Value is saved. Click "Reveal secrets" to edit it.')
                            : ''
                    }
                />

                <SwitchInput
                    label={isArabic ? 'وضع الاختبار (Sandbox)' : 'Sandbox mode'}
                    name="sandbox"
                    checked={Boolean(safe.sandbox)}
                    onChange={(e) => handleField('sandbox')(e.target.checked)}
                    disabled={disabled}
                    helperText={
                        isArabic
                            ? 'فعّل وضع الاختبار للتجارب، وعطّله للإنتاج.'
                            : 'Sandbox = on for testing, off for production traffic.'
                    }
                />
            </Stack>
        </Paper>
    );
};

export default PaymobSettingsCard;
